import React, { FC } from 'react'
import classnames from 'classnames'
import { useHistory } from 'react-router-dom'

// Components
import { MenuTooltip } from 'components'

// Types
import { AnchorType, GameFlowSteps, IUser } from 'types'

// Styles
import { Avatar, Theme, makeStyles, createStyles } from '@material-ui/core'
import {
  StyledMenuList,
  StyledMenuListHeader,
  StyledMenuListIcon,
  StyledMenuListItem,
  StyledOpenRightDrawer
} from './Styled'

import { graphQLMutation } from 'graphql/helpers'
import { createUserInteraction } from 'graphql/mutations'

// Images
import demoTvIcon from 'assets/demoTvIcon.svg'
import mailIcon from 'assets/mailIcon.svg'
import sessionIcon from 'assets/breakout.svg'
import infoIcon from 'assets/infoIcon.svg'
import gradCapIcon from 'assets/gradCapIcon.svg'
import supportIcon from 'assets/supportIcon.svg'
import liveStreamIcon from 'assets/liveStream.svg'

interface MenuListProps {
  user?: IUser
  anchor: AnchorType
  drawerOpen: boolean
  toggleMenuDrawer: () => void
  toggleTutorial: () => void
  toggleProfileDrawer: () => void
  toggleIntroTutorial: () => void
  setGameState: (state: GameFlowSteps) => void
}

const cloudfrontBase = 'https://dx2ge6d9z64m9.cloudfront.net/public/'

export const MenuList: FC<MenuListProps> = ({
  drawerOpen,
  toggleMenuDrawer,
  user,
  setGameState,
  toggleIntroTutorial,
  toggleProfileDrawer
}) => {
  const classes = useStyles()
  const history = useHistory()
  const openProfileDrawer = () => {
    if (!drawerOpen) {
      toggleMenuDrawer()
    }
    toggleProfileDrawer()
  }

  const handleMenuDrawerClose = () => {
    if (drawerOpen) {
      toggleMenuDrawer()
    }
  }

  const createInteraction = (link: string) => {
    graphQLMutation(createUserInteraction, {
      name: link,
      trigger: 'clicked',
      type: 'navigation',
      userId: user?.id
    })
  }

  return (
    <StyledOpenRightDrawer>
      <div onClick={() => openProfileDrawer()}>
        <StyledMenuListHeader>
          <Avatar className='header-avatar' src={`${cloudfrontBase}${user?.avatar}`} alt={user?.firstName}>
            {user?.firstName?.substring(0, 1)}
          </Avatar>
          <aside>
            <span className='header-title'>Welcome, {user?.firstName}</span>
            <small className='header-subtitle'>{user?.company}</small>
          </aside>
        </StyledMenuListHeader>
      </div>

      <StyledMenuList className={drawerOpen ? 'drawer-open' : 'drawer-close'}>
        <StyledMenuListItem
          onClick={() => {
            handleMenuDrawerClose()
            createInteraction('eventReplay')
            setGameState(GameFlowSteps.EventReplay)
          }}
        >
          <MenuTooltip drawerOpen={drawerOpen} title='Event replay' placement='left'>
            <StyledMenuListIcon>
              <img src={liveStreamIcon} alt='Event replay icon' width='25' />
            </StyledMenuListIcon>
          </MenuTooltip>
          <div className={classnames([classes.menuText], { [classes.menuTextDrawerClosed]: !drawerOpen })}>
            Event replay
          </div>
        </StyledMenuListItem>
        <StyledMenuListItem
          onClick={() => {
            handleMenuDrawerClose()
            createInteraction('explore')
            setGameState(GameFlowSteps.Explore)
          }}
        >
          <MenuTooltip drawerOpen={drawerOpen} title='Demos' placement='left'>
            <StyledMenuListIcon>
              <img src={demoTvIcon} alt='Demos icon' width='23' />
            </StyledMenuListIcon>
          </MenuTooltip>
          <div className={classnames([classes.menuText], { [classes.menuTextDrawerClosed]: !drawerOpen })}>Demos</div>
        </StyledMenuListItem>
        <StyledMenuListItem
          onClick={() => {
            handleMenuDrawerClose()
            createInteraction('sessions')
            setGameState(GameFlowSteps.Sessions)
          }}
        >
          <MenuTooltip drawerOpen={drawerOpen} title='Breakout sessions' placement='left'>
            <StyledMenuListIcon>
              <img src={sessionIcon} alt='Breakout Session icon' width='16' />
            </StyledMenuListIcon>
          </MenuTooltip>
          <div className={classnames([classes.menuText], { [classes.menuTextDrawerClosed]: !drawerOpen })}>
            Breakout sessions
          </div>
        </StyledMenuListItem>
        <StyledMenuListItem
          onClick={() => {
            handleMenuDrawerClose()
            createInteraction('contactUs')
            setGameState(GameFlowSteps.Connect)
          }}
        >
          <MenuTooltip drawerOpen={drawerOpen} title='Contact us' placement='left'>
            <StyledMenuListIcon>
              <img src={mailIcon} alt='Mail icon' width='19' />
            </StyledMenuListIcon>
          </MenuTooltip>
          <div className={classnames([classes.menuText], { [classes.menuTextDrawerClosed]: !drawerOpen })}>
            Contact us
          </div>
        </StyledMenuListItem>
        <StyledMenuListItem
          onClick={() => {
            handleMenuDrawerClose()
            createInteraction('aboutTheEvent')
            window.postMessage('{"command":"about"}', '*')
          }}
        >
          <MenuTooltip drawerOpen={drawerOpen} title='About the event' placement='left'>
            <StyledMenuListIcon>
              <img src={infoIcon} alt='Info icon' width='19' />
            </StyledMenuListIcon>
          </MenuTooltip>
          <div className={classnames([classes.menuText], { [classes.menuTextDrawerClosed]: !drawerOpen })}>
            About the event
          </div>
        </StyledMenuListItem>
        <StyledMenuListItem
          onClick={() => {
            createInteraction('tutorial')
            toggleIntroTutorial()
          }}
        >
          <MenuTooltip drawerOpen={drawerOpen} title='Tutorial' placement='left'>
            <StyledMenuListIcon>
              <img src={gradCapIcon} alt='Grad cap icon' width='24' />
            </StyledMenuListIcon>
          </MenuTooltip>
          <div className={classnames([classes.menuText], { [classes.menuTextDrawerClosed]: !drawerOpen })}>
            Tutorial
          </div>
        </StyledMenuListItem>
        <StyledMenuListItem
          onClick={() => {
            createInteraction('support')
            window.postMessage('{"command":"support"}', '*')
          }}
        >
          <MenuTooltip drawerOpen={drawerOpen} title='Support' placement='left'>
            <StyledMenuListIcon>
              <img src={supportIcon} alt='Support icon' width='19' />
            </StyledMenuListIcon>
          </MenuTooltip>
          <div className={classnames([classes.menuText], { [classes.menuTextDrawerClosed]: !drawerOpen })}>Support</div>
        </StyledMenuListItem>
      </StyledMenuList>
    </StyledOpenRightDrawer>
  )
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuTextDrawerClosed: {
      display: 'none'
    },
    menuText: {
      textOverflow: 'ellipsis'
    }
  })
)
